import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { EditorContent, NodeViewProps, NodeViewWrapper, ReactNodeViewRenderer, useEditor } from '@tiptap/react';
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { Node as PMNode } from '@tiptap/pm/model';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { Markdown } from 'tiptap-markdown';

import * as api from '@/features/backlog/api';
import * as wikiApi from '@/features/wiki/api';
import { Attachment } from '@/features/backlog/types';
import AuthImage, { AuthMedia, attachmentIdFromSrc } from '@/components/ui/AuthImage';
import { useLightbox } from '@/components/lightbox';
import { Popover } from '@/components/ui/Popover';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/components/ui/cn';

// Lets a description image open the lightbox (provided per-editor with the current image set).
const OpenImageCtx = createContext<(src: string) => void>(() => {});

/** Image + video files are the media we upload-and-embed from a paste / drop / picker. */
const isMediaFile = (f: File) => f.type.startsWith('image/') || f.type.startsWith('video/');

/** A leading YAML front-matter block (--- … ---) is agent-pipeline metadata (branch/spec/plan/owner),
 *  not prose. Split it off so the rich view shows only the body; the raw block is kept and re-prepended
 *  verbatim on save (and stays visible/editable in the Markdown view). A lone `---` (no closing fence)
 *  is a horizontal rule, not front-matter, so it isn't matched. */
const FRONTMATTER_RE = /^---[ \t]*\r?\n[\s\S]*?\r?\n---[ \t]*\r?\n?/;
function splitFrontmatter(md: string): { frontmatter: string; body: string } {
  const m = md.match(FRONTMATTER_RE);
  return m ? { frontmatter: m[0], body: md.slice(m[0].length) } : { frontmatter: '', body: md };
}

/** Attachment-backed images are fetched with auth (see AuthImage) rather than a bare <img>; a click
 *  opens the lightbox. */
function ImageNodeView({ node }: NodeViewProps) {
  const openSrc = useContext(OpenImageCtx);
  const src = node.attrs.src as string;
  return (
    <NodeViewWrapper className="md-img" data-drag-handle>
      <AuthMedia src={src} alt={node.attrs.alt as string} onImageClick={() => openSrc(src)} />
    </NodeViewWrapper>
  );
}
const AuthImageExtension = Image.extend({
  addNodeView() {
    return ReactNodeViewRenderer(ImageNodeView);
  },
});

/**
 * Keep a normal, clickable text line at the very end of the document AND between adjacent block media,
 * so there's always a place to drop a vertical caret and type near an image/video. Without it, a
 * description that is just an image (or several) has nowhere to click for a caret — clicking the image
 * only opens the lightbox. Mirrors the trailing paragraph the media-insert already leaves behind.
 */
/**
 * Positions (descending, so inserting in order keeps earlier ones valid) where an empty paragraph must
 * go to guarantee a clickable caret line ABOVE and BELOW block media:
 *  - above: before a LEADING image (between-image gaps are covered by the 'below' rule of the image
 *    before them, so only the very first node needs this);
 *  - below: after a TRAILING image, or one immediately followed by another block image.
 */
function mediaSpacingInserts(doc: PMNode): number[] {
  if (doc.childCount === 0) return [];
  const isMedia = (index: number) => {
    const n = index >= 0 && index < doc.childCount ? doc.child(index) : null;
    return !!n && n.type.name === 'image';
  };
  const inserts: number[] = [];
  doc.forEach((node, offset, index) => {
    if (node.type.name !== 'image') return;
    if (index === 0) inserts.push(offset);
    if (index === doc.childCount - 1 || isMedia(index + 1)) inserts.push(offset + node.nodeSize);
  });
  inserts.sort((a, b) => b - a);
  return inserts;
}

// ---- wiki-URL chip -------------------------------------------------------------------------------
// A same-origin (or any-host) /wiki/<space>/<page> | /wiki/p/<id> link inside a description renders as a
// chip (doc icon + page title + external-link). Implemented as a DECORATION so the STORED markdown stays
// a plain URL (round-trips untouched) — the raw URL text is hidden and a widget chip drawn in its place.
const WIKI_PATH_RE = /^\/wiki\/(?:p\/([0-9a-fA-F-]{36})|([a-z0-9][a-z0-9-]*)\/([a-z0-9][a-z0-9-]*))$/;

type WikiTarget = { kind: 'id'; id: string } | { kind: 'slug'; space: string; page: string };
function parseWikiHref(href: string): WikiTarget | null {
  try {
    const u = new URL(href, window.location.origin);
    const m = u.pathname.match(WIKI_PATH_RE);
    if (!m) return null;
    return m[1] ? { kind: 'id', id: m[1] } : { kind: 'slug', space: m[2], page: m[3] };
  } catch {
    return null;
  }
}
const isWikiHref = (href: string) => !!parseWikiHref(href);
function wikiHrefFallbackLabel(href: string): string {
  const t = parseWikiHref(href);
  if (t?.kind === 'slug') return t.page.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return 'Wiki page';
}
async function resolveWikiTitle(href: string): Promise<string | null> {
  const t = parseWikiHref(href);
  if (!t) return null;
  try {
    if (t.kind === 'id') return (await wikiApi.getWikiPage(t.id)).title || null;
    const space = await wikiApi.getWikiSpaceBySlug(t.space);
    const page = await wikiApi.getWikiPageBySlug(space.id, t.page);
    return page.title || null;
  } catch {
    return null;
  }
}

function findWikiLinks(doc: PMNode): { from: number; to: number; href: string }[] {
  const out: { from: number; to: number; href: string }[] = [];
  doc.descendants((node, pos) => {
    if (!node.isText) return;
    const link = node.marks.find((m) => m.type.name === 'link');
    const href = link?.attrs.href as string | undefined;
    if (href && isWikiHref(href)) out.push({ from: pos, to: pos + node.nodeSize, href });
  });
  return out;
}

function renderWikiChip(href: string, title: string | null): HTMLElement {
  const el = document.createElement('span');
  el.className = 'md-wiki-chip';
  el.contentEditable = 'false';
  el.setAttribute('data-wiki-chip', '');
  el.title = title || href;
  el.innerHTML =
    '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>' +
    '<span class="md-wiki-chip-title"></span>' +
    '<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><path d="M15 3h6v6"/><path d="M10 14L21 3"/></svg>';
  (el.querySelector('.md-wiki-chip-title') as HTMLElement).textContent = title || wikiHrefFallbackLabel(href);
  el.addEventListener('mousedown', (e) => e.preventDefault());
  el.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(href, '_blank', 'noopener,noreferrer');
  });
  return el;
}

const wikiChipKey = new PluginKey('wikiChip');
function buildWikiChipDecos(doc: PMNode, cache: Map<string, string | null>): DecorationSet {
  const decos: Decoration[] = [];
  for (const { from, to, href } of findWikiLinks(doc)) {
    decos.push(Decoration.inline(from, to, { class: 'md-wiki-chip-src' }));
    decos.push(Decoration.widget(from, () => renderWikiChip(href, cache.get(href) ?? null), { side: -1, ignoreSelection: true }));
  }
  return DecorationSet.create(doc, decos);
}

const WikiChip = Extension.create({
  name: 'wikiChip',
  addProseMirrorPlugins() {
    const cache = new Map<string, string | null>(); // href -> title (null = pending/unresolved)
    return [
      new Plugin({
        key: wikiChipKey,
        state: {
          init: (_c, state) => buildWikiChipDecos(state.doc, cache),
          apply: (tr, old, _oldState, newState) =>
            tr.docChanged || tr.getMeta(wikiChipKey) ? buildWikiChipDecos(newState.doc, cache) : old.map(tr.mapping, tr.doc),
        },
        props: {
          decorations(state) {
            return wikiChipKey.getState(state);
          },
        },
        view: (view) => {
          const resolve = () => {
            for (const { href } of findWikiLinks(view.state.doc)) {
              if (cache.has(href)) continue;
              cache.set(href, null); // mark seen (pending) — shows the fallback label meanwhile
              void resolveWikiTitle(href).then((title) => {
                if (title && cache.get(href) !== title) {
                  cache.set(href, title);
                  view.dispatch(view.state.tr.setMeta(wikiChipKey, true).setMeta('addToHistory', false));
                }
              });
            }
          };
          resolve();
          return { update: resolve };
        },
      }),
    ];
  },
});

const MediaSpacing = Extension.create({
  name: 'mediaSpacing',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        appendTransaction: (trs, _oldState, state) => {
          if (!trs.some((t) => t.docChanged)) return null;
          const para = state.schema.nodes.paragraph;
          if (!para) return null;
          const inserts = mediaSpacingInserts(state.doc);
          if (!inserts.length) return null;
          const tr = state.tr;
          for (const pos of inserts) tr.insert(pos, para.create());
          return tr.setMeta('addToHistory', false);
        },
      }),
    ];
  },
  // appendTransaction only fires on transactions AFTER init, so the INITIAL content (loaded via the
  // editor's `content` option) is never normalised — an existing item whose description ends in an
  // image would open with no caret line below it. Apply the same spacing once on create, suppressing
  // the update (preventUpdate) so merely OPENING an item never marks the description dirty.
  onCreate() {
    const { state, view } = this.editor;
    const para = state.schema.nodes.paragraph;
    if (!para) return;
    const inserts = mediaSpacingInserts(state.doc);
    if (!inserts.length) return;
    const tr = state.tr;
    for (const pos of inserts) tr.insert(pos, para.create());
    view.dispatch(tr.setMeta('addToHistory', false).setMeta('preventUpdate', true));
  },
});

/** Toolbar icons — stroke-based, matching the app's icon system (viewBox 24, currentColor). */
const Ico = ({ children }: { children: React.ReactNode }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const IcoBold = () => <Ico><path d="M7 5h7a3.5 3.5 0 0 1 0 7H7zM7 12h8a3.5 3.5 0 0 1 0 7H7z" /></Ico>;
const IcoItalic = () => <Ico><path d="M19 5h-7M12 19H5M15 5l-4 14" /></Ico>;
const IcoStrike = () => <Ico><path d="M16 5H9.5A3 3 0 0 0 7 9.5M14 12a3.5 3.5 0 0 1 0 7H7M4 12h16" /></Ico>;
const IcoCode = () => <Ico><path d="M16 18l6-6-6-6M8 6l-6 6 6 6" /></Ico>;
const IcoH1 = () => <Ico><path d="M4 12h8M4 18V6M12 18V6M17 12l3-2v8" /></Ico>;
const IcoH2 = () => <Ico><path d="M4 12h8M4 18V6M12 18V6M17 10.5c0-1 1-1.7 2-1.7s2 .7 2 1.7c0 1.7-4 2.8-4 5.5h4" /></Ico>;
const IcoH3 = () => <Ico><path d="M4 12h8M4 18V6M12 18V6M17.3 9.2c1.5-1 3.4 0 3.4 1.4a1.8 1.8 0 0 1-1.8 1.8M16.8 15.6c1.7 1.2 3.9.3 3.9-1.3a1.8 1.8 0 0 0-1.8-1.7" /></Ico>;
const IcoList = () => (
  <Ico>
    <path d="M8 6h13M8 12h13M8 18h13" />
    <circle cx="3.5" cy="6" r="1" fill="currentColor" stroke="none" />
    <circle cx="3.5" cy="12" r="1" fill="currentColor" stroke="none" />
    <circle cx="3.5" cy="18" r="1" fill="currentColor" stroke="none" />
  </Ico>
);
const IcoOrdered = () => <Ico><path d="M10 6h11M10 12h11M10 18h11M4 6h1v4M4 10h2M6 18H4c0-1 2-1.6 2-2.6S5 14 4 14.5" /></Ico>;
const IcoQuote = () => <Ico><path d="M5 5v14M9 8h10M9 12h10M9 16h6" /></Ico>;
const IcoCodeBlock = () => <Ico><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M9 9l-2 3 2 3M15 9l2 3-2 3" /></Ico>;
const IcoLink = () => <Ico><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></Ico>;
const IcoImage = () => <Ico><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.6" /><path d="M21 15l-4.5-4.5L6 21" /></Ico>;
const IcoTable = () => <Ico><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 10h18M3 15h18M9 4v16M15 4v16" /></Ico>;
const IcoCheckList = () => <Ico><rect x="3" y="5" width="7" height="7" rx="1.5" /><path d="M4.5 8.5 6 10l2.5-3M13 7h8M13 17h8" /><rect x="3" y="14" width="7" height="7" rx="1.5" /></Ico>;
const IcoChevron = () => <Ico><path d="M6 9l6 6 6-6" /></Ico>;
const IcoPlay = () => <Ico><path d="M8 5v14l11-7z" /></Ico>;

type Props = {
  value: string;
  onChange: (markdown: string) => void;
  placeholder?: string;
  /** Item id images/videos attach to; when absent (unsaved item), media insertion is disabled. */
  itemId?: string | null;
  /** Existing image + video attachments to offer in the "insert from attachments" picker. */
  mediaAttachments?: Attachment[];
  /** Fired after a paste/drop/pick upload so the drawer can refresh its Attachments list. */
  onImageUploaded?: (att: Attachment) => void;
};

/**
 * A premium description editor: a TipTap rich-text surface by default, toggleable to raw Markdown.
 * Content is stored as Markdown (via tiptap-markdown), so it round-trips cleanly between the two
 * views. Images upload to the bucket (as item attachments) on paste / drop / pick / insert-from-
 * attachments and render inline through {@link AuthImage} (token-authed fetch).
 */
export default function MarkdownEditor({ value, onChange, placeholder, itemId, mediaAttachments = [], onImageUploaded }: Props) {
  const [mode, setMode] = useState<'rich' | 'md'>('rich');
  const [uploading, setUploading] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);
  const itemIdRef = useRef(itemId);
  itemIdRef.current = itemId;
  const cbRef = useRef(onImageUploaded);
  cbRef.current = onImageUploaded;
  // A stable handle to the latest "insert media" fn, so the editor's paste/drop handlers (captured
  // once at editor init) always call the current one.
  const insertMediaRef = useRef<(src: string) => void>(() => {});
  // The current description's leading front-matter block, held aside so the rich editor shows only the
  // body while the block is re-prepended verbatim on every save — editing in rich mode never drops it.
  const frontmatterRef = useRef(splitFrontmatter(value).frontmatter);

  // Upload a file as an attachment on the current item and return its API URL (or null if there's
  // no saved item yet / the upload failed).
  const uploadImage = useCallback(async (file: File): Promise<string | null> => {
    const id = itemIdRef.current;
    if (!id) return null;
    setUploading((n) => n + 1);
    try {
      const att = await api.uploadAttachment(id, file);
      cbRef.current?.(att);
      return api.attachmentUrl(att.id);
    } catch {
      return null;
    } finally {
      setUploading((n) => n - 1);
    }
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, codeBlock: { HTMLAttributes: { class: 'md-code' } } }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { target: '_blank', rel: 'noreferrer noopener', class: 'md-link' } }),
      WikiChip,
      AuthImageExtension,
      MediaSpacing,
      Table.configure({ resizable: true, HTMLAttributes: { class: 'wiki-table' } }),
      TableRow,
      TableHeader,
      TableCell,
      TaskList,
      TaskItem.configure({ nested: true }),
      Placeholder.configure({ placeholder: placeholder ?? 'Write a description…' }),
      Markdown.configure({ html: false, transformPastedText: true, linkify: true }),
    ],
    content: splitFrontmatter(value).body,
    editorProps: {
      attributes: { class: 'md-content' },
      // Click a link — even while the description is editable — opens it in a new tab instead of
      // dropping the caret into it. (openOnClick stays off so TipTap doesn't also handle it.)
      handleClick: (_view, _pos, event) => {
        const a = (event.target as HTMLElement | null)?.closest?.('a');
        const href = a?.getAttribute('href');
        if (a && href) {
          window.open(href, '_blank', 'noopener,noreferrer');
          return true;
        }
        return false;
      },
      // Intercept pasted / dropped image + video files → upload → insert; let everything else through.
      handlePaste: (_view, event) => {
        const files = Array.from(event.clipboardData?.files ?? []).filter(isMediaFile);
        if (files.length === 0) return false;
        event.preventDefault();
        files.forEach((f) => {
          void uploadImage(f).then((url) => url && insertMediaRef.current(url));
        });
        return true;
      },
      handleDrop: (_view, event) => {
        const files = Array.from((event as DragEvent).dataTransfer?.files ?? []).filter(isMediaFile);
        if (files.length === 0) return false;
        event.preventDefault();
        files.forEach((f) => {
          void uploadImage(f).then((url) => url && insertMediaRef.current(url));
        });
        return true;
      },
    },
    // Re-prepend the front-matter the rich view hides, so the stored markdown keeps it intact.
    onUpdate: ({ editor }) => onChange(frontmatterRef.current + editor.storage.markdown.getMarkdown()),
  });
  const editorRef = useRef(editor);
  editorRef.current = editor;

  // Insert media (image or video) at the caret, then an empty paragraph, so there's always a normal
  // text line beneath the block to place the caret on — otherwise sitting next to a bare block node
  // shows ProseMirror's horizontal gap-cursor instead of a vertical caret.
  const insertMedia = useCallback((src: string) => {
    editorRef.current?.chain().focus().insertContent([{ type: 'image', attrs: { src } }, { type: 'paragraph' }]).run();
  }, []);
  insertMediaRef.current = insertMedia;

  // Sync external value changes (switching items, autosave reconcile) into the editor without
  // firing onUpdate — but only when it genuinely differs from what the editor already holds, so a
  // keystroke's own round-trip doesn't reset the cursor.
  useEffect(() => {
    if (!editor) return;
    const { frontmatter, body } = splitFrontmatter(value);
    frontmatterRef.current = frontmatter;
    // The editor holds only the body; reset it only when the incoming body actually differs, so a
    // keystroke's own round-trip (or a front-matter-only change) never resets the cursor.
    if (body !== editor.storage.markdown.getMarkdown()) editor.commands.setContent(body, false);
  }, [value, editor]);

  const onPickFiles = (files: FileList | null) => {
    Array.from(files ?? [])
      .filter(isMediaFile)
      .forEach((f) => void uploadImage(f).then((url) => url && insertMedia(url)));
  };

  const canImage = !!itemId;
  const isRich = mode === 'rich';

  // Click a description image → open it in the lightbox, with the item's image attachments as the
  // strip (falling back to a single synthetic entry if the click predates an attachments refresh).
  const openLightbox = useLightbox();
  const openImage = useCallback(
    (src: string) => {
      const id = attachmentIdFromSrc(src);
      if (!id || !openLightbox) return;
      // Only images belong in the lightbox strip (videos play inline), so filter the media list.
      const imgs = mediaAttachments.filter((a) => a.content_type?.startsWith('image/'));
      const list = imgs.some((a) => a.id === id)
        ? imgs
        : [{ id, content_type: 'image/*', filename: 'Image', item_id: '', size: 0, storage_key: '', created_at: '', url: '' } as Attachment];
      openLightbox(list, id);
    },
    [openLightbox, mediaAttachments],
  );

  return (
    <OpenImageCtx.Provider value={openImage}>
    <div className="overflow-hidden rounded-xl border border-line bg-surface-raised focus-within:border-line-strong">
      <Toolbar
        editor={editor}
        isRich={isRich}
        onToggleMode={() => {
          // Leaving MD mode: push the raw text back through the editor so rich view reflects it —
          // splitting off any front-matter the user edited there so rich mode keeps showing just the body.
          if (!isRich && editor) {
            const { frontmatter, body } = splitFrontmatter(value);
            frontmatterRef.current = frontmatter;
            editor.commands.setContent(body, false);
          }
          setMode(isRich ? 'md' : 'rich');
        }}
        canImage={canImage}
        uploading={uploading > 0}
        onImageButton={() => fileRef.current?.click()}
        mediaAttachments={mediaAttachments}
        onInsertAttachment={(a) => insertMedia(api.attachmentUrl(a.id))}
      />
      <input
        ref={fileRef}
        type="file"
        accept="image/*,video/*"
        multiple
        className="hidden"
        onChange={(e) => {
          onPickFiles(e.target.files);
          e.target.value = '';
        }}
      />
      {isRich ? (
        // Content scrolls within the card while the toolbar (the row above) stays put — the controls
        // remain visible on a long description, and it works in the modal, dock, and full-screen page
        // alike (no reliance on a sticky element / outer scroll container).
        <EditorContent editor={editor} className="md-editor max-h-[60vh] overflow-y-auto px-3 py-2.5" />
      ) : (
        <textarea
          className="md-editor block w-full resize-y bg-transparent px-3 py-2.5 font-mono text-[12.5px] leading-relaxed text-fg outline-none"
          rows={12}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder ?? 'Write Markdown…'}
          spellCheck={false}
        />
      )}
    </div>
    </OpenImageCtx.Provider>
  );
}

type ToolbarProps = {
  editor: ReturnType<typeof useEditor>;
  isRich: boolean;
  onToggleMode: () => void;
  canImage: boolean;
  uploading: boolean;
  onImageButton: () => void;
  mediaAttachments: Attachment[];
  onInsertAttachment: (a: Attachment) => void;
};

function Toolbar({ editor, isRich, onToggleMode, canImage, uploading, onImageButton, mediaAttachments, onInsertAttachment }: ToolbarProps) {
  const btn = (active: boolean) =>
    cn(
      'flex h-7 w-7 items-center justify-center rounded-md transition-colors',
      active ? 'bg-accent/15 text-accent' : 'text-fg-muted hover:bg-layer-strong hover:text-fg',
    );
  const run = (fn: () => void) => (e: React.MouseEvent) => {
    e.preventDefault();
    fn();
  };
  const disabled = !isRich || !editor;

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-line bg-surface-inset/70 px-1.5 py-1">
      {/* Rich-only formatting controls (dimmed in Markdown mode). */}
      <div className={cn('flex flex-wrap items-center gap-0.5', disabled && 'pointer-events-none opacity-40')}>
        <button type="button" className={btn(!!editor?.isActive('bold'))} onMouseDown={run(() => editor?.chain().focus().toggleBold().run())} title="Bold (⌘B)"><IcoBold /></button>
        <button type="button" className={btn(!!editor?.isActive('italic'))} onMouseDown={run(() => editor?.chain().focus().toggleItalic().run())} title="Italic (⌘I)"><IcoItalic /></button>
        <button type="button" className={btn(!!editor?.isActive('strike'))} onMouseDown={run(() => editor?.chain().focus().toggleStrike().run())} title="Strikethrough"><IcoStrike /></button>
        <button type="button" className={btn(!!editor?.isActive('code'))} onMouseDown={run(() => editor?.chain().focus().toggleCode().run())} title="Inline code"><IcoCode /></button>
        <span className="mx-0.5 h-4 w-px bg-line" />
        <button type="button" className={btn(!!editor?.isActive('heading', { level: 1 }))} onMouseDown={run(() => editor?.chain().focus().toggleHeading({ level: 1 }).run())} title="Heading 1"><IcoH1 /></button>
        <button type="button" className={btn(!!editor?.isActive('heading', { level: 2 }))} onMouseDown={run(() => editor?.chain().focus().toggleHeading({ level: 2 }).run())} title="Heading 2"><IcoH2 /></button>
        <button type="button" className={btn(!!editor?.isActive('heading', { level: 3 }))} onMouseDown={run(() => editor?.chain().focus().toggleHeading({ level: 3 }).run())} title="Heading 3"><IcoH3 /></button>
        <span className="mx-0.5 h-4 w-px bg-line" />
        <button type="button" className={btn(!!editor?.isActive('bulletList'))} onMouseDown={run(() => editor?.chain().focus().toggleBulletList().run())} title="Bullet list"><IcoList /></button>
        <button type="button" className={btn(!!editor?.isActive('orderedList'))} onMouseDown={run(() => editor?.chain().focus().toggleOrderedList().run())} title="Numbered list"><IcoOrdered /></button>
        <button type="button" className={btn(!!editor?.isActive('taskList'))} onMouseDown={run(() => editor?.chain().focus().toggleTaskList().run())} title="Checklist"><IcoCheckList /></button>
        <button type="button" className={btn(!!editor?.isActive('table'))} onMouseDown={run(() => editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run())} title="Insert table"><IcoTable /></button>
        <button type="button" className={btn(!!editor?.isActive('blockquote'))} onMouseDown={run(() => editor?.chain().focus().toggleBlockquote().run())} title="Quote"><IcoQuote /></button>
        <button type="button" className={btn(!!editor?.isActive('codeBlock'))} onMouseDown={run(() => editor?.chain().focus().toggleCodeBlock().run())} title="Code block"><IcoCodeBlock /></button>
        <span className="mx-0.5 h-4 w-px bg-line" />
        <button
          type="button"
          className={btn(!!editor?.isActive('link'))}
          onMouseDown={run(() => {
            const prev = editor?.getAttributes('link').href as string | undefined;
            const url = window.prompt('Link URL', prev ?? 'https://');
            if (url === null) return;
            if (url === '') editor?.chain().focus().unsetLink().run();
            else editor?.chain().focus().setLink({ href: url }).run();
          })}
          title="Link"
        >
          <IcoLink />
        </button>
        {/* Media: pick a file, or insert an existing image/video attachment. */}
        <button
          type="button"
          disabled={!canImage}
          className={cn(btn(false), !canImage && 'cursor-not-allowed opacity-40')}
          onMouseDown={run(() => canImage && onImageButton())}
          title={canImage ? 'Upload & insert image or video' : 'Save the item first to add media'}
        >
          <IcoImage />
        </button>
        {canImage && mediaAttachments.length > 0 ? (
          <Popover
            align="left"
            trigger={({ toggle }) => (
              <button type="button" className={cn(btn(false), 'w-5')} onMouseDown={run(toggle)} title="Insert from attachments">
                <IcoChevron />
              </button>
            )}
          >
            {(close) => (
              <div className="max-h-[260px] w-[220px] overflow-auto p-1">
                <div className="px-2 pb-1 pt-0.5 text-[11px] font-semibold uppercase tracking-wide text-fg-faint">From attachments</div>
                {mediaAttachments.map((a) => {
                  const isVideo = a.content_type?.startsWith('video/');
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onMouseDown={run(() => {
                        onInsertAttachment(a);
                        close();
                      })}
                      className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12px] text-fg-soft transition-colors hover:bg-layer"
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded bg-layer ring-1 ring-line">
                        {isVideo ? <span className="text-fg-faint"><IcoPlay /></span> : <AuthImage src={api.attachmentUrl(a.id)} className="h-full w-full object-cover" />}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{a.filename}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </Popover>
        ) : null}
      </div>
      <div className="ml-auto flex items-center gap-1.5">
        {uploading ? <Spinner className="h-3.5 w-3.5 text-fg-faint" /> : null}
        {/* Rich ⇄ Markdown toggle. */}
        <div className="inline-flex items-center rounded-full bg-layer p-0.5 text-[11px] font-semibold">
          <button
            type="button"
            onMouseDown={run(() => isRich || onToggleMode())}
            className={cn('rounded-full px-2 py-0.5 transition-colors', isRich ? 'bg-surface-raised text-fg-strong shadow-sm' : 'text-fg-muted hover:text-fg')}
          >
            Rich
          </button>
          <button
            type="button"
            onMouseDown={run(() => !isRich || onToggleMode())}
            className={cn('rounded-full px-2 py-0.5 transition-colors', !isRich ? 'bg-surface-raised text-fg-strong shadow-sm' : 'text-fg-muted hover:text-fg')}
          >
            MD
          </button>
        </div>
      </div>
    </div>
  );
}
